import { Head, useForm, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft } from 'lucide-react'
import { FormEvent, useState } from 'react'
import CarrierAppointmentsEditor, { CarrierAppointmentInput, CarrierOption } from '@/components/CarrierAppointmentsEditor'

interface Agent {
  id: number
  email: string
  first_name: string
  last_name: string
  parent_agent_id: number | null
  override_percentage: number
  phone: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  zip: string
  npn_number: string | null
  excluded_from_equity?: boolean
  equity_credits_count?: number
  pending_equity_credits_count?: number
  carrier_appointments?: Array<{
    id: number
    carrier_id: number
    carrier_name: string | null
    producer_number: string
  }>
}

interface ParentAgent {
  id: number
  name: string
  status?: string
}

// Sponsors can be at any stage of onboarding, so label the ones who aren't
// fully active yet — an imported agent is a valid sponsor but still a prospect.
const sponsorLabel = (pa: ParentAgent) => {
  if (!pa.status || pa.status === 'active') return pa.name
  const readable = pa.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return `${pa.name} (${readable})`
}

interface Props extends PageProps {
  agent: Agent | null
  parent_agents: ParentAgent[]
  carriers: CarrierOption[]
  // False when the organization hides commissions and equity from this viewer,
  // which also hides the equity-program controls below.
  show_financials?: boolean
  sendgrid_connected?: boolean
  docuseal_connected?: boolean
  docuseal_default_template_id?: string | null
}

export default function AgentForm({
  agent,
  parent_agents,
  carriers,
  show_financials = true,
  sendgrid_connected = false,
  docuseal_connected = false,
  docuseal_default_template_id
}: Props) {
  const isEdit = !!agent
  const hasDocusealTemplate = docuseal_connected && !!docuseal_default_template_id
  const [appointments, setAppointments] = useState<CarrierAppointmentInput[]>(
    (agent?.carrier_appointments ?? []).map(a => ({
      id: a.id,
      carrier_id: a.carrier_id,
      producer_number: a.producer_number
    }))
  )
  const pageErrors = (usePage().props.errors as Record<string, string>) || {}
  const { data, setData, processing, errors: formErrors } = useForm({
    first_name: agent?.first_name || '',
    last_name: agent?.last_name || '',
    email: agent?.email || '',
    parent_agent_id: agent?.parent_agent_id?.toString() || '',
    override_percentage: agent?.override_percentage?.toString() || '10',
    phone: agent?.phone || '',
    address_line1: agent?.address_line1 || '',
    address_line2: agent?.address_line2 || '',
    city: agent?.city || '',
    state: agent?.state || '',
    zip: agent?.zip || '',
    npn_number: agent?.npn_number || '',
    excluded_from_equity: agent?.excluded_from_equity || false,
    send_welcome_email: sendgrid_connected,
    send_contract: hasDocusealTemplate,
    initial_password: ''
  })

  const errors: Record<string, string> = { ...pageErrors, ...(formErrors as Record<string, string>) }

  const existingEquityCount =
    (agent?.equity_credits_count || 0) + (agent?.pending_equity_credits_count || 0)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (
      data.excluded_from_equity &&
      !agent?.excluded_from_equity &&
      existingEquityCount > 0
    ) {
      const ok = confirm(
        `Excluding ${data.first_name || 'this agent'} from the equity program will permanently delete ` +
          `their ${existingEquityCount} existing equity credit row${existingEquityCount === 1 ? '' : 's'} ` +
          `(earned + pending). New commissions going forward will not accrue equity for this agent. Continue?`
      )
      if (!ok) return
    }
    const agentAttributes = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      parent_agent_id: data.parent_agent_id || null,
      override_percentage: data.override_percentage,
      phone: data.phone,
      address_line1: data.address_line1,
      address_line2: data.address_line2,
      city: data.city,
      state: data.state,
      zip: data.zip,
      npn_number: data.npn_number,
      excluded_from_equity: data.excluded_from_equity,
      carrier_appointments_attributes: appointments
        .filter(a => a.id || (!a._destroy && (a.carrier_id !== '' || a.producer_number.trim() !== '')))
        .map(a => ({
          id: a.id,
          carrier_id: a.carrier_id === '' ? null : a.carrier_id,
          producer_number: a.producer_number,
          _destroy: a._destroy ? '1' : undefined
        }))
    }
    if (isEdit) {
      router.put(`/agents/${agent.id}`, { agent: agentAttributes })
    } else {
      router.post('/agents', {
        agent: agentAttributes,
        send_welcome_email: data.send_welcome_email,
        send_contract: data.send_contract,
        initial_password: data.initial_password
      })
    }
  }

  return (
    <AppLayout>
      <Head title={isEdit ? `Edit Agent: ${agent.first_name} ${agent.last_name}` : 'New Agent'} />

      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/agents" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
            {isEdit ? 'Edit Agent' : 'New Agent'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          {(errors as Record<string, string>).base && (
            <div className="rounded border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-status-error">
              {(errors as Record<string, string>).base}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={data.first_name}
                onChange={(e) => setData('first_name', e.target.value)}
                className="input-field w-full"
                required
              />
              {errors.first_name && <p className="mt-1 text-sm text-status-error">{errors.first_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={data.last_name}
                onChange={(e) => setData('last_name', e.target.value)}
                className="input-field w-full"
                required
              />
              {errors.last_name && <p className="mt-1 text-sm text-status-error">{errors.last_name}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
              Email *
            </label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              className="input-field w-full"
              required
              disabled={isEdit}
            />
            {errors.email && <p className="mt-1 text-sm text-status-error">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
              Sponsoring Agent
            </label>
            <select
              value={data.parent_agent_id}
              onChange={(e) => setData('parent_agent_id', e.target.value)}
              className="input-field w-full"
            >
              <option value="">None (top-level agent)</option>
              {parent_agents.map((pa) => (
                <option key={pa.id} value={pa.id}>{sponsorLabel(pa)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-chestnut-charcoal/60">
              Select the agent who sponsors this agent, if applicable. Agents at any stage of
              onboarding — including imported prospects — can sponsor a downline.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                Override Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={data.override_percentage}
                  onChange={(e) => setData('override_percentage', e.target.value)}
                  className="input-field w-full pr-8"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60">
                  %
                </span>
              </div>
              <p className="mt-1 text-xs text-chestnut-charcoal/60">
                Override percentage earned from sponsored agents
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={data.phone}
                onChange={(e) => setData('phone', e.target.value)}
                className="input-field w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
              NPN Number
            </label>
            <input
              type="text"
              value={data.npn_number}
              onChange={(e) => setData('npn_number', e.target.value)}
              className="input-field w-full"
              placeholder="National Producer Number"
            />
          </div>

          {show_financials && (
          <div className="border-t border-chestnut-gray pt-6">
            <h3 className="text-lg font-semibold text-chestnut-charcoal mb-4">Equity Program</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.excluded_from_equity}
                onChange={(e) => setData('excluded_from_equity', e.target.checked)}
                className="mt-1 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-semibold text-chestnut-charcoal">
                  Exclude from equity program
                </span>
                <span className="block text-xs text-chestnut-charcoal/60 mt-0.5">
                  Internal/admin users who receive commissions but should not accrue equity.
                  No equity credits (production or override) will be created for this agent on
                  any commission going forward.
                  {isEdit && existingEquityCount > 0 && (
                    <span className="block mt-1 text-burgundy">
                      Heads up: {existingEquityCount} existing equity row
                      {existingEquityCount === 1 ? '' : 's'} will be deleted when you save
                      with this enabled.
                    </span>
                  )}
                </span>
              </span>
            </label>
          </div>
          )}

          <div className="border-t border-chestnut-gray pt-6">
            <h3 className="text-lg font-semibold text-chestnut-charcoal mb-2">Carrier Appointments</h3>
            <p className="text-sm text-chestnut-charcoal/60 mb-4">
              Producer / writing numbers for each carrier this agent is appointed with. Incoming FFR commissions are auto-matched to this agent when the writing-agent ID matches any number here. Multiple entries per carrier are allowed.
            </p>
            <CarrierAppointmentsEditor
              appointments={appointments}
              carriers={carriers}
              onChange={setAppointments}
            />
          </div>

          <div className="border-t border-chestnut-gray pt-6">
            <h3 className="text-lg font-semibold text-chestnut-charcoal mb-4">Address</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={data.address_line1}
                  onChange={(e) => setData('address_line1', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={data.address_line2}
                  onChange={(e) => setData('address_line2', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-3">
                  <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={data.city}
                    onChange={(e) => setData('city', e.target.value)}
                    className="input-field w-full"
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={data.state}
                    onChange={(e) => setData('state', e.target.value)}
                    className="input-field w-full"
                    maxLength={2}
                    placeholder="TX"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={data.zip}
                    onChange={(e) => setData('zip', e.target.value)}
                    className="input-field w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {!isEdit && (
            <div className="border-t border-chestnut-gray pt-6">
              <h3 className="text-lg font-semibold text-chestnut-charcoal mb-2">Onboarding</h3>
              <div className="mb-4 text-sm">
                <p className="text-chestnut-charcoal/70">
                  SendGrid:{' '}
                  <span className={sendgrid_connected ? 'font-semibold text-status-success' : 'font-semibold text-status-error'}>
                    {sendgrid_connected ? 'Connected' : 'Not connected'}
                  </span>
                  {' · '}DocuSeal:{' '}
                  <span className={hasDocusealTemplate ? 'font-semibold text-status-success' : 'font-semibold text-status-error'}>
                    {docuseal_connected
                      ? (hasDocusealTemplate ? 'Connected' : 'Connected (no default template)')
                      : 'Not connected'}
                  </span>
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={data.send_welcome_email}
                    disabled={!sendgrid_connected}
                    onChange={(e) => setData('send_welcome_email', e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-semibold text-chestnut-charcoal">Email login details to the agent now</span>
                    <span className="block text-xs text-chestnut-charcoal/60">
                      {sendgrid_connected
                        ? 'Sends a welcome email with their sign-in email, password and a sign-in link.'
                        : 'Connect SendGrid in Integrations to enable this.'}
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={data.send_contract}
                    disabled={!hasDocusealTemplate}
                    onChange={(e) => setData('send_contract', e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-semibold text-chestnut-charcoal">Send DocuSeal contract now</span>
                    <span className="block text-xs text-chestnut-charcoal/60">
                      {hasDocusealTemplate
                        ? 'Uses your default contract template; sets status to Contract Pending.'
                        : docuseal_connected
                          ? 'Set a default contract template on the Documents page to enable this.'
                          : 'Connect DocuSeal in Integrations to enable this.'}
                    </span>
                  </span>
                </label>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
                  Initial password <span className="font-normal text-chestnut-charcoal/60">(optional)</span>
                </label>
                <input
                  type="text"
                  value={data.initial_password}
                  onChange={(e) => setData('initial_password', e.target.value)}
                  className="input-field w-full"
                  placeholder="Leave blank to auto-generate"
                  autoComplete="off"
                  minLength={8}
                />
                <p className="mt-1 text-xs text-chestnut-charcoal/60">
                  If set, we'll show it once on the next screen so you can hand it to the agent. Must be at least 8 characters. The agent can change it any time via the reset-password link.
                </p>
                {errors.initial_password && (
                  <p className="mt-1 text-sm text-status-error">{errors.initial_password}</p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4 border-t border-chestnut-gray">
            <Link href="/agents" className="btn-secondary">
              Cancel
            </Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Saving...' : (isEdit ? 'Update Agent' : 'Create Agent')}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
